import React from 'react';
import type { EmThread } from '../../email/types';
import { Icon } from '../Icon/Icon';
import { EmailTag } from './EmailTag';

export interface EmailConvRowProps {
  thread: EmThread;
  active: boolean;
  starred: boolean;
  onStar: () => void;
  onClick: () => void;
}

export function EmailConvRow({ thread, active, starred, onStar, onClick }: EmailConvRowProps) {
  const emergency = thread.tags.includes('emergency');
  const draft = thread.aiState === 'drafted_low_conf';

  return (
    <div
      className={`em-conv ${active ? 'active' : ''} ${emergency ? 'emergency' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      <div className="em-conv-hd">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onStar();
          }}
          title={starred ? 'Unstar' : 'Star'}
          style={{
            border: 0,
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            alignSelf: 'flex-start',
            marginTop: 1,
            flex: 'none',
          }}
        >
          <Icon
            kind="star"
            size={15}
            stroke={1.9}
            style={starred ? { fill: 'var(--gold)', color: 'var(--gold)' } : { color: 'var(--ink-4)' }}
          />
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {emergency ? <span className="em-emerg-dot" style={{ alignSelf: 'center' }} /> : null}
            <span
              style={{
                fontSize: 13,
                fontWeight: thread.unread ? 700 : 600,
                color: 'var(--ink-0)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {thread.subjectTr || thread.subject}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
            <span
              style={{
                fontSize: 11.5,
                color: 'var(--ink-2)',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {thread.name}
            </span>
            {thread.lang === 'Spanish' ? (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'var(--ai-3)',
                  background: 'var(--ai-tint)',
                  padding: '1px 5px',
                  borderRadius: 4,
                  flex: 'none',
                }}
              >
                ES
              </span>
            ) : null}
            {thread.verified === 'unknown' ? (
              <Icon kind="shield-off" size={11} style={{ color: 'var(--ink-4)', flex: 'none' }} />
            ) : null}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flex: 'none' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-4)' }}>{thread.when}</span>
          {thread.unread ? <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--ai-3)' }} /> : null}
        </div>
      </div>
      <div className="em-conv-bd">
        <div
          style={{
            fontSize: 12.5,
            color: thread.unread ? 'var(--ink-1)' : 'var(--ink-2)',
            lineHeight: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {thread.snippetTr || thread.snippet}
        </div>
        <div style={{ display: 'flex', gap: 5, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}>
          {thread.tags.slice(0, 2).map((k) => (
            <EmailTag key={k} tagKey={k} sm />
          ))}
          {draft ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--gold-tint)',
                color: 'var(--gold)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <Icon kind="pencil" size={9} stroke={2.2} />
              Draft held
            </span>
          ) : null}
          {thread.aiState === 'replied' ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--success-tint)',
                color: 'var(--success)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <Icon kind="check" size={9} stroke={2.4} />
              Replied
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
